import { Eye, KeyRound, ShieldCheck, UserCheck } from 'lucide-react';
import PublicSiteLayout from '../components/PublicSiteLayout';

const boundaries = [
  [UserCheck, 'Authenticated family workspace', 'Family workspace pages, tasks, calendars, lists, and processed communications require a signed-in account. They are not intended as public website content.'],
  [Eye, 'Review before family action', 'Suggested reminders, tasks, and calendar items are designed to wait for a parent to approve, edit, or dismiss them.'],
  [KeyRound, 'Keep account access personal', 'Use a unique password, protect access to the email account associated with Little Adda, and sign out on shared devices.'],
  [ShieldCheck, 'Treat source communication as authoritative', 'Automated extraction can be incomplete or incorrect. Review consequential dates, payments, forms, and schedule changes against the original school message.'],
];

function PrivacySecurityPage() {
  return (
    <PublicSiteLayout
      eyebrow="Privacy and security"
      title="Family information deserves clear boundaries and visible control."
      lede="This page explains the product principles and user-facing safeguards currently represented by Little Adda. It does not replace the formal terms or privacy notices presented during product use."
    >
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="grid gap-4 md:grid-cols-2">
          {boundaries.map(([Icon, title, copy]) => (
            <article key={title} className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-subtle sm:p-7">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-800"><Icon className="h-6 w-6" /></span>
              <h2 className="mt-5 text-xl font-black">{title}</h2>
              <p className="mt-3 leading-8 text-slate-600">{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-amber-200 bg-amber-50 py-16">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-amber-800">Important limitation</p>
          <h2 className="mt-4 text-3xl font-black">Do not rely on an extracted suggestion as the only record.</h2>
          <p className="mt-5 max-w-3xl text-lg leading-9 text-slate-700">Schools and educators remain the authoritative source for deadlines, attendance requirements, emergencies, payments, permissions, and schedule changes. Little Adda is intended to help organize communication, not replace it.</p>
        </div>
      </section>

      <section className="mx-auto grid max-w-5xl gap-8 px-5 py-16 sm:px-8 sm:py-24 md:grid-cols-2">
        <div>
          <h2 className="text-2xl font-black">Public website content</h2>
          <p className="mt-4 leading-8 text-slate-600">Product pages, documentation, and general explanations may be indexed by search engines and AI search tools. Do not place private family information into public website fields unless the product explicitly identifies the field and its purpose.</p>
        </div>
        <div>
          <h2 className="text-2xl font-black">Private application content</h2>
          <p className="mt-4 leading-8 text-slate-600">Signed-in routes are marked against public indexing, and the application requires authentication before family workspace content is displayed. Search exclusion is an additional boundary, not a substitute for access control.</p>
        </div>
      </section>
    </PublicSiteLayout>
  );
}

export default PrivacySecurityPage;
